import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DICTS as BAKED_DICTS, LANGUAGES as BAKED_LANGS, DEFAULT_LANG } from "@/i18n/translations";
import api from "@/lib/api";

const I18nContext = createContext(null);
const LOCAL_KEY = "bh_lang";
const LOCAL_PACKS_KEY = "bh_i18n_packs";

export function I18nProvider({ children }) {
  // Dicts = baked packs merged with any server-generated packs (fr, es, de, ...).
  const [dicts, setDicts] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(LOCAL_PACKS_KEY) || "{}");
      const merged = { ...BAKED_DICTS };
      for (const [code, info] of Object.entries(cached)) {
        if (info?.pack) merged[code] = info.pack;
      }
      return merged;
    } catch { return { ...BAKED_DICTS }; }
  });
  const [extraLangs, setExtraLangs] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(LOCAL_PACKS_KEY) || "{}");
      return Object.entries(cached).map(([code, info]) => ({ code, label: info?.label || code }));
    } catch { return []; }
  });
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    return localStorage.getItem(LOCAL_KEY) || DEFAULT_LANG;
  });

  // Fetch any server-generated packs on mount so a new language added by one
  // user is available to every other user without a code deploy.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/i18n/packs");
        if (!data || typeof data !== "object") return;
        setDicts((prev) => {
          const next = { ...prev };
          for (const [code, info] of Object.entries(data)) if (info?.pack) next[code] = info.pack;
          return next;
        });
        setExtraLangs(Object.entries(data).map(([code, info]) => ({ code, label: info?.label || code })));
        try { localStorage.setItem(LOCAL_PACKS_KEY, JSON.stringify(data)); } catch { /* ignore */ }
      } catch { /* ignore — offline fallback is fine */ }
    })();
  }, []);

  useEffect(() => { try { localStorage.setItem(LOCAL_KEY, lang); } catch { /* ignore */ } }, [lang]);

  const setLang = useCallback((next) => {
    setLangState((cur) => (dicts[next] ? next : cur));
  }, [dicts]);

  const addLanguage = useCallback(async (code, name) => {
    const normalized = (code || "").trim().toLowerCase();
    if (!normalized) throw new Error("lang_code required");
    if (dicts[normalized]) { setLangState(normalized); return { code: normalized }; }
    const { data } = await api.post("/i18n/auto-translate", {
      lang_code: normalized,
      lang_name: name || normalized,
      source_pack: BAKED_DICTS[DEFAULT_LANG],
    });
    setDicts((prev) => ({ ...prev, [data.lang_code]: data.pack }));
    setExtraLangs((prev) => {
      const without = prev.filter((l) => l.code !== data.lang_code);
      return [...without, { code: data.lang_code, label: data.lang_name || data.lang_code }];
    });
    try {
      const cached = JSON.parse(localStorage.getItem(LOCAL_PACKS_KEY) || "{}");
      cached[data.lang_code] = { label: data.lang_name, pack: data.pack };
      localStorage.setItem(LOCAL_PACKS_KEY, JSON.stringify(cached));
    } catch { /* ignore */ }
    setLangState(data.lang_code);
    return data;
  }, [dicts]);

  const t = useCallback((key, fallback) => {
    const parts = key.split(".");
    const dict = dicts[lang] || dicts[DEFAULT_LANG];
    let cur = dict;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else { cur = undefined; break; }
    }
    if (typeof cur === "string") return cur;
    let fb = dicts[DEFAULT_LANG];
    for (const p of parts) {
      if (fb && typeof fb === "object" && p in fb) fb = fb[p];
      else { fb = undefined; break; }
    }
    if (typeof fb === "string") return fb;
    return fallback ?? key;
  }, [lang, dicts]);

  const languages = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const l of BAKED_LANGS) { seen.add(l.code); list.push(l); }
    for (const l of extraLangs) if (!seen.has(l.code)) { seen.add(l.code); list.push(l); }
    return list;
  }, [extraLangs]);

  const value = useMemo(() => ({ lang, setLang, t, languages, addLanguage }), [lang, setLang, t, languages, addLanguage]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
};
export const useT = () => useI18n().t;
